import { format, parse } from 'date-fns'
import { es } from 'date-fns/locale'
import { translate } from '../i18n'

interface DateFormatProps {
    date?: Date
    pattern?: string
    locale?: Locale
    zone?: boolean
}

// https://date-fns.org/v2.29.3/docs/format

const DEFAULT_DATE_FORMAT = 'dd-MM-yyyy'

const DEFAULT_DATETIME_FORMAT = 'dd-MM-yyyy HH:mm:ss'

const HUMAN_DATE_FORMAT = 'P'

const HUMAN_DATETIME_FORMAT = 'P, kk:mm'

const PICKER_DATE_FORMAT = 'dd MMM, yyyy'

const PICKER_DATETIME_FORMAT = 'dd MMM, yyyy, kk:mm'

const parseDateTime = (
    input?: string,
    format: string = DEFAULT_DATETIME_FORMAT
): Date | null => {
    return parseDate(input, format)
}

const parseDate = (
    input?: string,
    format: string = DEFAULT_DATE_FORMAT
): Date | null => {
    if (!input) return null

    return parse(input, format, new Date())
}

const formatDateTime = ({
    date,
    pattern = DEFAULT_DATETIME_FORMAT,
    locale = es,
    zone,
}: DateFormatProps): string => {
    return formatDate({ date, pattern, locale, zone })
}

const formatDate = ({
    date,
    pattern = DEFAULT_DATE_FORMAT,
    locale = es,
    zone,
}: DateFormatProps) => {
    if (date) {
        let suffix = ''
        if (zone) {
            suffix = ` ${format(date, 'zzzz', { locale: locale })}`
        }

        return `${format(date, pattern, {
            locale: locale,
        })}${suffix}`
    }

    return ''
}

const formatDateRange = (from: Date, to?: Date, locale?: Locale) => {
    if (!to || to === from) {
        return formatDate({ date: from, locale })
    }

    const fFrom = formatDate({ date: from, locale })
    const fTo = formatDate({ date: to, locale })

    if (fFrom === fTo) {
        return fFrom
    }

    return translate('date_ranges.value', {
        from: fFrom,
        to: fTo,
    })
}

export type { DateFormatProps }

export {
    DEFAULT_DATE_FORMAT,
    DEFAULT_DATETIME_FORMAT,
    HUMAN_DATE_FORMAT,
    HUMAN_DATETIME_FORMAT,
    PICKER_DATE_FORMAT,
    PICKER_DATETIME_FORMAT,
    parseDate,
    parseDateTime,
    formatDate,
    formatDateTime,
    formatDateRange,
}
